import type {
  AssignmentDetailApiResponse,
  AssignmentListApiResponse,
  SubmissionAssetApiResponse
} from "@ts/interfaces/assignments/api/assignmentsApiInterface";
import { del, get, post } from "./httpClient";

export const getAssignmentsList = async (
  gradeCode: number,
  term?: number
): Promise<AssignmentListApiResponse> => {
  const url = term
    ? `/assignments/grade/${gradeCode}?term=${term}`
    : `/assignments/grade/${gradeCode}`;
  return await get<AssignmentListApiResponse>(url, { suppressGlobalErrors: true });
};

const isEmptyObject = (v: unknown): boolean =>
  v != null && typeof v === "object" && Object.keys(v).length === 0;

export const getAssignmentDetail = async (
  assignmentId: string,
  gradeCode: number,
  term: number
): Promise<AssignmentDetailApiResponse> => {
  const res = await get<AssignmentDetailApiResponse>(
    `/assignments/${assignmentId}?grade_code=${gradeCode}&term=${term}`
  );
  const a = res.assignment;
  if (isEmptyObject(a.assignment_blocks)) a.assignment_blocks = undefined;
  if (isEmptyObject(a.rubric_blocks)) a.rubric_blocks = undefined;
  if (isEmptyObject(a.support_guide_blocks)) a.support_guide_blocks = undefined;
  return res;
};

export const createSubmission = async (
  assignmentId: string
): Promise<{ submission_uuid: string; assets: SubmissionAssetApiResponse[] }> => {
  return await post<{ submission_uuid: string; assets: SubmissionAssetApiResponse[] }>(
    `/assignments/${assignmentId}/submissions`
  );
};

// createSubmission is idempotent — returns the existing in-progress submission.
export const refreshInProgressSubmission = createSubmission;

export const finalizeSubmission = async (
  submissionUuid: string
): Promise<void> => {
  await post(
    `/assignments/submissions/${submissionUuid}/finalize`,
    undefined,
    { suppressGlobalErrors: true }
  );
};

export const createExemption = async (
  assignmentId: string,
  reason: string
): Promise<{ uuid?: string; status?: string; message?: string }> => {
  return await post<{ uuid?: string; status?: string; message?: string }>(
    `/assignments/${assignmentId}/exemption`,
    { reason }
  );
};

/**
 * Delete an exemption by submission UUID.
 *
 * Note: Despite the parameter name, this accepts the *submission UUID*
 * (from detail.exemptions[0].uuid), not the exemption UUID itself.
 * Portal looks up the exemption via the submission.
 */
export const deleteExemption = async (
  exemptionId: string
): Promise<{ message: string }> => {
  return await del<{ message: string }>(
    `/assignments/exemptions/${exemptionId}`
  );
};

/**
 * Request a pre-signed S3 upload URL for a submission asset.
 * The MFE then PUTs the file directly to S3 using the returned URL.
 */
export const createSubmissionAsset = async (
  submissionUuid: string,
  deliverableUuid: string,
  fileName: string,
  fileSize: number,
  mimeType: string,
  url?: string
): Promise<{
  type?: "file" | "url";
  file_upload_url?: string;
  download_link?: string;
  file_upload_uuid?: string;
  file_name?: string;
  file_asset_id?: number;
}> => {
  const payload = url
    ? { deliverable_uuid: deliverableUuid, url }
    : { deliverable_uuid: deliverableUuid, file_name: fileName, file_size: fileSize, mime_type: mimeType };
  return await post(`/assignments/submissions/${submissionUuid}/asset`, payload, { suppressGlobalErrors: true });
};

/**
 * Upload a file directly to S3 using a pre-signed PUT URL.
 * This is called after createSubmissionAsset returns the signed URL.
 */
export const uploadFileToSignedUrl = async (
  signedUrl: string,
  file: File
): Promise<void> => {
  const response = await fetch(signedUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type || "application/octet-stream"
    }
  });
  if (!response.ok) {
    throw new Error(
      `Upload failed: ${response.status} ${response.statusText}`
    );
  }
};

export const deleteSubmissionAsset = async (
  submissionUuid: string,
  assetId: number
): Promise<void> => {
  await del(
    `/assignments/submissions/${submissionUuid}/asset/${assetId}`,
    undefined,
    { suppressGlobalErrors: true }
  );
};

export const assignmentsApi = {
  getAssignmentsList,
  getAssignmentDetail,
  createSubmission,
  refreshInProgressSubmission,
  finalizeSubmission,
  createExemption,
  deleteExemption,
  createSubmissionAsset,
  uploadFileToSignedUrl,
  deleteSubmissionAsset
};
